import useTranslation from 'next-translate/useTranslation';
import Head from 'next/head';
import { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { FunctionComponent, useEffect } from 'react';

import useLoggedUser from '../../../hooks/use-logged-user';
import usePageViewEvent from '../../../hooks/use-page-view-event';
import useHreflang from '../../../utils/seo/hreflang';
import useOGMeta from '../../../utils/seo/ogmeta';
import useSchema from '../../../utils/seo/schema-org/schema';
import { useAppContext } from '../../providers/app-context';
import cleanFAQs from '../../../utils/cleanFaqs';

const FaqSchema: FunctionComponent<{
  faqsForSchema: Array<TFaqs>;
  breadcrumbs: Array<{
    href: LinkProps;
    text: string;
  }>;
}> = ({ breadcrumbs, faqsForSchema }) => {
  const { t } = useTranslation();
  const user = useLoggedUser();
  const { businessValues } = useAppContext();

  const name = t('faqs:head.title');

  const description = t('faqs:head.description', {
    totalStockAvailable: businessValues.totalStockAvailable.defaultValue,
  });

  const { locale = 'en', asPath } = useRouter();
  const localePath = locale !== 'en' ? `/${locale}` : '';

  const og = useOGMeta({
    title: name,
    description,
    url: process.env.NEXT_PUBLIC_HOST + localePath + asPath,
  });

  const alternateLinks = useHreflang({
    locale,
    route: 'faqs',
  });

  usePageViewEvent('name');

  const cleanFaqs = cleanFAQs(faqsForSchema);

  return (
    <Head>
      <title>{name}</title>
      <meta name="description" content={description} />
      {alternateLinks}
      {og}

      <script
        {...useSchema({
          name,
          description,
          breadcrumbs,
          extraSchema: [
            {
              '@type': 'FAQPage', // All of the following faqs should be translated to the respective property "inLanguage" presented above
              mainEntity: [
                ...cleanFaqs
              ],
            },
          ],
        })}
      />
    </Head>
  );
};

export default FaqSchema;
